import { Ticket, CheckInData } from '../types/ticket';
import { RegistrationData } from '../types/index';

const SPREADSHEET_ID = '1JRtHDPYX9AISlQ6CNfhDBVuSnzGKa7RKf_q-qJ6LYXM';
const SHEET_NAME = 'DataFormDangKy';
const RANGE = 'A:F'; // Columns A to F: STT, Họ và tên, Số điện thoại, Link Shop, Email, Vấn đề

// Cache for session storage
const CACHE_KEY = 'tickets_data';
const CACHE_TIMESTAMP_KEY = 'tickets_cache_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export interface GoogleSheetsResponse {
  values: string[][];
}

interface QueuedRequest extends CheckInData {
  resolve: (value: boolean) => void;
  reject: (error: Error) => void;
}

export class GoogleSheetsService {
  private static instance: GoogleSheetsService;
  private requestQueue: QueuedRequest[] = [];
  private isProcessingQueue = false;
  private readonly MAX_CONCURRENT_REQUESTS = 3;
  private activeRequests = 0;
  
  static getInstance(): GoogleSheetsService {
    if (!GoogleSheetsService.instance) {
      GoogleSheetsService.instance = new GoogleSheetsService();
    }
    return GoogleSheetsService.instance;
  }

  /**
   * Get all tickets data with caching
   */
  async getAllTickets(): Promise<Ticket[]> {
    try {
      // Check cache first
      const cachedData = this.getCachedData();
      if (cachedData) {
        console.log('Using cached tickets data');
        return cachedData;
      }
      
      // Fetch fresh data from Google Sheets only if no cache
      console.log('Fetching fresh tickets from Google Sheets...');
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values/${SHEET_NAME}!${RANGE}?key=AIzaSyDW-UUUQc4AFLpO3kMk_lB_RkSF_sHZyo4`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: GoogleSheetsResponse = await response.json();
      const tickets = this.parseTicketsData(data.values);
      
      // Cache the fresh data
      this.cacheData(tickets);
      
      return tickets;
    } catch (error) {
      console.error('Error fetching tickets:', error);
      throw new Error('Không thể tải dữ liệu vé. Vui lòng thử lại sau.');
    }
  }

  /**
   * Get all checked-in tickets from Google Apps Script
   */
  async getAllCheckedInTickets(): Promise<Set<string>> {
    try {
      console.log('Fetching fresh checked-in tickets...');
      
      // Use the direct Apps Script URL (no redirects)
      const CHECKIN_API_URL = 'https://script.google.com/macros/s/AKfycbxPYAasXvI8B2I7119AlmQbM9kM25iRaMkof7tUu5Z7503mRnkOZd3GAQbn2eTbKgc3/exec';
      
      const response = await fetch(CHECKIN_API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Raw API response:', result);
      
      if (result.success && result.data) {
        console.log('Check-in data:', result.data);
        // Extract all maCheckin from checked-in records
        const maCheckinList = result.data.map((record: any) => record.maCheckin).filter((maCheckin: any) => maCheckin);
        console.log('Extracted maCheckin list:', maCheckinList);
        const checkedInSet = new Set<string>(maCheckinList);
        console.log('Final checked-in set:', checkedInSet);
        
        return checkedInSet;
      } else {
        console.log('No success or no data in result');
        return new Set();
      }
      
    } catch (error) {
      console.error('Error getting checked-in tickets:', error);
      return new Set();
    }
  }

  /**
   * Get tickets by phone number with check-in status
   */
  async getTicketsByPhone(phoneNumber: string): Promise<Ticket[]> {
    const allTickets = await this.getAllTickets();
    const normalizedPhone = this.normalizePhoneNumber(phoneNumber);
    
    // Filter tickets by phone number
    const filteredTickets = allTickets.filter(ticket => {
      const ticketPhone = this.normalizePhoneNumber(ticket.sdt);
      
      // Exact match
      if (ticketPhone === normalizedPhone) {
        return true;
      }
      
      // Match without leading 0
      const phoneWithoutZero = normalizedPhone.startsWith('0') ? normalizedPhone.substring(1) : normalizedPhone;
      const ticketPhoneWithoutZero = ticketPhone.startsWith('0') ? ticketPhone.substring(1) : ticketPhone;
      
      return phoneWithoutZero === ticketPhoneWithoutZero;
    });
    
    // If no tickets found, return empty array
    if (filteredTickets.length === 0) {
      return [];
    }
    
    // Get all checked-in tickets (single API call)
    console.log('Getting checked-in tickets...');
    const checkedInTickets = await this.getAllCheckedInTickets();
    console.log('Retrieved checked-in tickets:', checkedInTickets);
    
    // Map tickets with check-in status
    const ticketsWithStatus = filteredTickets.map(ticket => {
      const isCheckedIn = checkedInTickets.has(ticket.maCheckin);
      console.log(`Ticket ${ticket.maCheckin} (${ticket.hoTen}) - Checked in: ${isCheckedIn}`);
      return {
        ...ticket,
        checkedIn: isCheckedIn
      };
    });
    
    console.log('Final tickets with status:', ticketsWithStatus);
    return ticketsWithStatus;
  }


  /**
   * Get tickets by email with check-in status
   */
  async getTicketsByEmail(email: string): Promise<Ticket[]> {
    const allTickets = await this.getAllTickets();
    const normalizedEmail = email.toLowerCase().trim();
    
    // Filter tickets by email
    const filteredTickets = allTickets.filter(ticket => {
      const ticketEmail = ticket.email.toLowerCase().trim();
      return ticketEmail === normalizedEmail;
    });
    
    // If no tickets found, return empty array
    if (filteredTickets.length === 0) {
      return [];
    }
    
    // Get all checked-in tickets (single API call)
    console.log('Getting checked-in tickets...');
    const checkedInTickets = await this.getAllCheckedInTickets();
    console.log('Retrieved checked-in tickets:', checkedInTickets);
    
    // Map tickets with check-in status
    const ticketsWithStatus = filteredTickets.map(ticket => {
      const isCheckedIn = checkedInTickets.has(ticket.maCheckin);
      console.log(`Ticket ${ticket.maCheckin} (${ticket.hoTen}) - Checked in: ${isCheckedIn}`);
      return {
        ...ticket,
        checkedIn: isCheckedIn
      };
    });
    
    console.log('Final tickets with status:', ticketsWithStatus);
    return ticketsWithStatus;
  }

  /**
   * Submit check-in data using JSONP (alternative method)
   */
  private submitCheckInJSONP(checkInData: CheckInData): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        const CHECKIN_API_URL = 'https://script.google.com/macros/s/AKfycbxPYAasXvI8B2I7119AlmQbM9kM25iRaMkof7tUu5Z7503mRnkOZd3GAQbn2eTbKgc3/exec';
        
        // Create a form and submit it
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = CHECKIN_API_URL;
        form.target = '_blank';
        
        // Add form fields
        const fields = {
          hoTen: checkInData.hoTen,
          email: checkInData.email,
          soDienThoai: checkInData.sdt,
          hangVe: checkInData.hangVe,
          maCheckin: checkInData.maCheckin,
        };
        
        Object.entries(fields).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        
        // Append to body and submit
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
        
        console.log('Check-in request sent via form submission');
        
        resolve(true);
        
      } catch (error) {
        console.error('Error submitting check-in via JSONP:', error);
        reject(error);
      }
    });
  }

  /**
   * Submit check-in data with queue management
   */
  async submitCheckIn(checkInData: CheckInData): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // Add to queue with callback
      const queuedRequest: QueuedRequest = {
        ...checkInData,
        resolve,
        reject
      };
      
      this.requestQueue.push(queuedRequest);
      
      // Start processing queue if not already running
      if (!this.isProcessingQueue) {
        this.processQueue();
      }
    });
  }

  /**
   * Process the request queue with concurrency control
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue) return;
    
    this.isProcessingQueue = true;
    console.log(`Starting queue processing. Queue size: ${this.requestQueue.length}`);
    
    while (this.requestQueue.length > 0) {
      // Wait if we have too many active requests
      while (this.activeRequests >= this.MAX_CONCURRENT_REQUESTS) {
        await this.delay(100);
      }
      
      // Process next request
      const request = this.requestQueue.shift();
      if (request) {
        this.processSingleRequest(request);
      }
    }
    
    this.isProcessingQueue = false;
    console.log('Queue processing completed');
  }

  /**
   * Process a single check-in request
   */
  private async processSingleRequest(request: QueuedRequest): Promise<void> {
    this.activeRequests++;
    
    try {
      const success = await this.attemptCheckInWithRetry(request);
      request.resolve(success);
    } catch (error) {
      request.reject(error as Error);
    } finally {
      this.activeRequests--;
    }
  }

  /**
   * Attempt check-in with retry mechanism
   */
  private async attemptCheckInWithRetry(checkInData: CheckInData): Promise<boolean> {
    const MAX_RETRIES = 3;
    const BASE_DELAY = 1000; // 1 second base delay
    
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.log(`Check-in attempt ${attempt}/${MAX_RETRIES} for ${checkInData.maCheckin}`);
        
        const success = await this.attemptCheckIn(checkInData);
        
        if (success) {
          console.log(`Check-in successful on attempt ${attempt}`);
          return true;
        }
        
        // If not successful and not last attempt, wait before retry
        if (attempt < MAX_RETRIES) {
          const delay = BASE_DELAY * Math.pow(2, attempt - 1); // Exponential backoff
          console.log(`Waiting ${delay}ms before retry...`);
          await this.delay(delay);
        }
        
      } catch (error) {
        console.error(`Check-in attempt ${attempt} failed:`, error);
        
        // If this is the last attempt, throw the error
        if (attempt === MAX_RETRIES) {
          throw new Error(`Không thể ghi nhận check-in sau ${MAX_RETRIES} lần thử. Vui lòng thử lại sau.`);
        }
        
        // Wait before retry
        const delay = BASE_DELAY * Math.pow(2, attempt - 1);
        console.log(`Waiting ${delay}ms before retry...`);
        await this.delay(delay);
      }
    }
    
    return false;
  }

  /**
   * Single attempt to submit check-in
   */
  private async attemptCheckIn(checkInData: CheckInData): Promise<boolean> {
    try {
      // Try fetch first with no-cors
      const CHECKIN_API_URL = 'https://script.google.com/macros/s/AKfycbxPYAasXvI8B2I7119AlmQbM9kM25iRaMkof7tUu5Z7503mRnkOZd3GAQbn2eTbKgc3/exec';
      
      await fetch(CHECKIN_API_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hoTen: checkInData.hoTen,
          email: checkInData.email,
          soDienThoai: checkInData.sdt,
          hangVe: checkInData.hangVe,
          maCheckin: checkInData.maCheckin,
        }),
      });

      // With no-cors mode, we can't read response body
      // If request doesn't throw error, assume success
      console.log('Check-in request sent successfully');
      
      return true;
      
    } catch (error) {
      console.error('Fetch method failed, trying form submission:', error);
      
      // Fallback to form submission method
      try {
        return await this.submitCheckInJSONP(checkInData);
      } catch (jsonpError) {
        console.error('Both methods failed:', jsonpError);
        throw jsonpError;
      }
    }
  }

  /**
   * Utility function to create delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Remove quotes from string values
   */
  private removeQuotes(value: string): string {
    if (typeof value === 'string') {
      return value.replace(/^"(.*)"$/, '$1');
    }
    return value;
  }

  /**
   * Parse raw Google Sheets data into Ticket objects
   */
  private parseTicketsData(values: string[][]): Ticket[] {
    if (!values || values.length < 2) {
      return [];
    }

    const rows = values.slice(1);

    return rows.map((row, index) => {
      // Map columns based on the actual sheet structure from the data
      const ticket: Ticket = {
        id: index + 1,
        hoTen: this.removeQuotes(row[1] || ''), // Column B - Họ tên
        sdt: this.removeQuotes(row[2] || ''), // Column C - SĐT
        email: this.removeQuotes(row[3] || ''), // Column D - Email
        maDonHang: this.removeQuotes(row[4] || ''), // Column E - Mã đơn hàng
        hangVe: this.removeQuotes(row[5] || ''), // Column F - Hạng vé (DIAMOND/GOLD/SILVER)
        tongTien: parseInt(this.removeQuotes(row[6])) || 0, // Column G - Tổng tiền
        ngayThanhToan: this.removeQuotes(row[7] || ''), // Column H - Ngày thanh toán
        trangThai: this.removeQuotes(row[8] || ''), // Column I - Trạng thái
        maCheckin: this.removeQuotes(row[9] || ''), // Column J - Mã checkin
        utmSource: this.removeQuotes(row[10] || ''), // Column K - UTM source
        utmPage: this.removeQuotes(row[11] || ''), // Column L - UTM page
        utmCampaign: this.removeQuotes(row[12] || ''), // Column M - UTM campaign
        ngayDangKy: this.removeQuotes(row[29] || ''), // Column AD - Ngày đăng ký
        checkedIn: false, // Will be updated based on check-in status
      };

      return ticket;
    }).filter(ticket => 
      ticket.hoTen && 
      ticket.sdt && 
      ticket.trangThai === 'Đã thanh toán'
    ); // Filter out empty rows and only show paid tickets
  }

  /**
   * Normalize phone number for comparison
   */
  private normalizePhoneNumber(phone: string): string {
    const cleanPhone = this.removeQuotes(phone);
    // Remove all non-digits
    const digitsOnly = cleanPhone.replace(/\D/g, '');
    
    // Handle Vietnamese phone numbers
    if (digitsOnly.startsWith('84')) {
      return '0' + digitsOnly.substring(2);
    } else if (digitsOnly.startsWith('0')) {
      return digitsOnly;
    } else {
      // If no leading 0, add it
      return '0' + digitsOnly;
    }
  }

  /**
   * Get cached data if still valid
   */
  private getCachedData(): Ticket[] | null {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cached && timestamp) {
        const cacheTime = parseInt(timestamp);
        const now = Date.now();
        
        if (now - cacheTime < CACHE_DURATION) {
          return JSON.parse(cached);
        }
      }
    } catch (error) {
      console.error('Error reading cache:', error);
    }
    
    return null;
  }

  /**
   * Cache data with timestamp
   */
  private cacheData(tickets: Ticket[]): void {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(tickets));
      sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      console.log(`Cached ${tickets.length} tickets`);
    } catch (error) {
      console.error('Error caching data:', error);
    }
  }


  /**
   * Clear cache
   */
  clearCache(): void {
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
    console.log('Cache cleared');
  }

  /**
   * Force refresh data (bypass cache)
   */
  async forceRefresh(): Promise<Ticket[]> {
    this.clearCache();
    return this.getAllTickets();
  }

  /**
   * Submit registration data using Google Apps Script
   */
  async submitRegistration(registrationData: RegistrationData): Promise<boolean> {
    try {
      // Use Google Apps Script for registration
      const REGISTRATION_API_URL = 'https://script.google.com/macros/s/AKfycbyeugdwQrS6mB9RwdhRLZYYk4OncZ3U79HOygEZuUn2Vq691OzQBMUViiaN1LAF1mrN/exec';
      
      // Try fetch first with no-cors (like check-in-final project)
      await fetch(REGISTRATION_API_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hoTen: registrationData.hoTen,
          soDienThoai: registrationData.soDienThoai,
          linkShop: registrationData.linkShop,
          email: registrationData.email,
          vanDeGapPhai: registrationData.vanDeGapPhai,
          timestamp: registrationData.timestamp,
        }),
      });

      // With no-cors mode, we can't read response body
      // If request doesn't throw error, assume success
      console.log('Registration request sent successfully');
      
      return true;
      
    } catch (error) {
      console.error('Fetch method failed, trying JSONP fallback:', error);
      
      // Fallback to JSONP method
      try {
        return await this.submitRegistrationJSONP(registrationData);
      } catch (jsonpError) {
        console.error('Both methods failed:', jsonpError);
        throw new Error('Không thể gửi thông tin đăng ký. Vui lòng thử lại sau.');
      }
    }
  }

  /**
   * Fallback method using JSONP
   */
  private async submitRegistrationJSONP(registrationData: RegistrationData): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const callbackName = `callback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Create script element
      const script = document.createElement('script');
      const REGISTRATION_API_URL = 'https://script.google.com/macros/s/AKfycbzR_xXVZbEMxZgknEGuRoe_Yq0b0wfCTMkQ7dq601WgutlzHUK0cdRrLmz9FjuM-GcP/exec';
      script.src = `${REGISTRATION_API_URL}?callback=${callbackName}&hoTen=${encodeURIComponent(registrationData.hoTen)}&soDienThoai=${encodeURIComponent(registrationData.soDienThoai)}&linkShop=${encodeURIComponent(registrationData.linkShop)}&email=${encodeURIComponent(registrationData.email)}&vanDeGapPhai=${encodeURIComponent(registrationData.vanDeGapPhai)}`;
      
      // Set up callback
      (window as any)[callbackName] = (response: any) => {
        try {
          if (response.success) {
            resolve(true);
          } else {
            reject(new Error(response.message || 'Unknown error'));
          }
        } catch (error) {
          reject(error);
        } finally {
          // Clean up
          document.head.removeChild(script);
          delete (window as any)[callbackName];
        }
      };
      
      // Handle script load error
      script.onerror = () => {
        document.head.removeChild(script);
        delete (window as any)[callbackName];
        reject(new Error('Failed to load script'));
      };
      
      // Add script to document
      document.head.appendChild(script);
      
      // Timeout after 10 seconds
      setTimeout(() => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
          delete (window as any)[callbackName];
          reject(new Error('Request timeout'));
        }
      }, 10000);
    });
  }
}

export default GoogleSheetsService;

